import React from 'react';

import { Switch } from '@mui/material';

import Checkbox from '@mui/material/Checkbox';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { AddConditionButton } from 'containers/MemberGroups/components/RuleBuilder/styles';

import { FieldName } from 'containers/PipelinesDetails/components/styles';

import { FieldConditionRule } from 'utils/api/modules/entities/entities.types';

import ConditionBuilder from './conditions/ConditionBuilder';
import { EventFieldCatalogue } from './conditions/eventFields';
import { committableRule, incompleteConditionIndexes, newRule } from './conditions/model';
import {
  ConditionBlock,
  ConditionError,
  ConditionSummary,
  FieldNameCell,
  FieldRow,
  FieldRowMain,
  RequiredLabel,
  RowControls,
} from './conditions/styles';

export interface RelevantFieldRowProps {
  fieldKey: string;
  name: string;
  checked: boolean;
  required: boolean;
  conditions?: FieldConditionRule;
  readBack: string;
  catalogue: EventFieldCatalogue;
  conditionsEnabled: boolean;
  onToggle: () => void;
  onRequiredChange: (required: boolean) => void;
  onConditionsChange: (conditions: FieldConditionRule | undefined) => void;
}

const RelevantFieldRow: React.FunctionComponent<RelevantFieldRowProps> = ({
  fieldKey,
  name,
  checked,
  required,
  conditions,
  readBack,
  catalogue,
  conditionsEnabled,
  onToggle,
  onRequiredChange,
  onConditionsChange,
}) => {
  const [editing, setEditing] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const [draft, setDraft] = React.useState<FieldConditionRule | undefined>(undefined);
  const [error, setError] = React.useState('');
  const [invalidRows, setInvalidRows] = React.useState<number[]>([]);
  const [valueLabels, setValueLabels] = React.useState<Record<string, string>>({});
  const blockId = `condition-editor-${React.useId()}`;

  React.useEffect(() => {
    if (!checked) {
      setEditing(false);
      setDraft(undefined);
      setError('');
      setInvalidRows([]);
    }
  }, [checked]);

  const startEditing = (): void => {
    setDraft(conditions ?? newRule());
    setEditing(true);
    setError('');
    setInvalidRows([]);
  };

  const closeEditor = (): void => {
    setEditing(false);
    setDraft(undefined);
    setError('');
    setInvalidRows([]);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const closeWithCheck = (): void => {
    const unfinished = incompleteConditionIndexes(draft);
    if (unfinished.length > 0) {
      setInvalidRows(unfinished);
      const names = unfinished
        .map((index) => draft?.conditions[index]?.attribute ?? '')
        .map((key) => (key ? (catalogue.labels[key] ?? key) : 'the unfinished row'));
      setError(
        names.length === 1
          ? `Finish the condition on ${names[0]}, or remove it. It will not be saved as it is.`
          : `Finish the conditions on ${names.slice(0, -1).join(', ')} and ${
              names[names.length - 1]
            }, or remove them. They will not be saved as they are.`
      );
      return;
    }
    closeEditor();
  };

  const onDraftChange = (next: FieldConditionRule, labels?: Record<string, string>): void => {
    setDraft(next);
    setError('');
    setInvalidRows([]);
    if (labels) {
      setValueLabels((previous) => ({ ...previous, ...labels }));
    }
    onConditionsChange(committableRule(next));
  };

  const requiredLabel = conditionsEnabled && (conditions || editing) ? 'Required when shown' : 'Required';

  return (
    <FieldRow
      data-testid={`relevant-field-row-${fieldKey}`}
      onKeyDown={
        editing
          ? (event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                closeWithCheck();
              }
            }
          : undefined
      }
    >
      <FieldRowMain>
        <Checkbox checked={checked} onChange={onToggle} inputProps={{ 'aria-label': name }} />
        <FieldNameCell>
          <FieldName>{name}</FieldName>
          {conditionsEnabled && !editing && readBack && (
            <ConditionSummary data-testid={`relevant-field-condition-summary-${fieldKey}`}>{readBack}</ConditionSummary>
          )}
        </FieldNameCell>
        {checked && (
          <RowControls>
            {conditionsEnabled && (
              <AddConditionButton
                ref={triggerRef}
                type="button"
                data-testid={`relevant-field-condition-toggle-${fieldKey}`}
                aria-expanded={editing}
                aria-controls={editing ? blockId : undefined}
                onClick={editing ? closeWithCheck : startEditing}
              >
                Only show if
                {editing ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </AddConditionButton>
            )}
            <Switch
              checked={required}
              onChange={(event) => onRequiredChange(event.target.checked)}
              color="primary"
              size="small"
              data-testid={`relevant-field-required-switch-${fieldKey}`}
              slotProps={{ input: { 'aria-label': `${name} ${requiredLabel.toLowerCase()}` } }}
            />
            <RequiredLabel>{requiredLabel}</RequiredLabel>
          </RowControls>
        )}
      </FieldRowMain>
      {conditionsEnabled && checked && editing && draft && (
        <ConditionBlock id={blockId} data-testid={`condition-editor-${fieldKey}`}>
          <ConditionBuilder
            rule={draft}
            catalogue={catalogue}
            ownerKey={fieldKey}
            labels={valueLabels}
            invalidRows={invalidRows}
            onChange={onDraftChange}
          />
          {error && (
            <ConditionError role="alert" data-testid="condition-error">
              {error}
            </ConditionError>
          )}
        </ConditionBlock>
      )}
    </FieldRow>
  );
};

export default RelevantFieldRow;
